import React, { useEffect, useMemo, useState } from 'react';
import CmsAPI from '../../../common/apis/CmsAPI';
import useObjectSimulation from '../../hooks/useObjectSimulation';
import styles from './VideoCallToAction.module.scss';
import { checkFontLoaded } from '../../utils/SimulatedItemsUtility';
import { stripHtmlTags } from '../../utils/stringHelper';
import { isHexColor } from '../../utils/CallToActionHelper';
import { checkIsFullyTransparent } from '../../utils/colorHelper';
import cx from 'classnames';
import { CARD_MIDDLE_PADDING_PERCENTAGE, CARD_PADDING_PERCENTAGE, POSITIONS } from '../../constants/callToActions';

const minFontSize = 15;
const CALL_ACTION_TRANSITION_TIME = 250;
const DEFAULT_CONTROLS_HEIGHT = { DESKTOP: 72, MOBILE: 52 }; // PlayerControls height in px
const PLAYER_BORDER_SIZE = 1;

const VideoCallToAction = ({
  parentNode,
  callAction,
  id,
  currentSeconds,
  isFullScreen = false,
  mixedOrientation,
  videoMaxDimensions,
  onPauseCallback,
  isMobileView,
  isControlsVisible,
  isNewActionTimeline,
  multiplyMode = false,
  isSharedPage,
}) => {
  const [customStyles, setCustomStyles] = useState({});
  const [opacity, setOpacity] = useState(0);
  const [updateSimulation, setUpdateSimulation] = useState(false);

  const buttonLabel = useMemo(() => stripHtmlTags(callAction.text).replaceAll('\n', '<br/>'), [callAction]);

  const { objectPosition,
    recalculateVuToPx,
    loading,
    videoContentWidth,
    videoContentHeight, } = useObjectSimulation({
      parentNode,
      id,
      position: callAction?.position,
      mode: 'callAction',
      mixedOrientation,
      maxDimensions: videoMaxDimensions,
      updateSimulation,
      multiplyMode,
    });

  const checkURLFormat = url => {
    const re = /^(http(s):\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/gm;
    return re.test(url);
  };

  const handleClick = () => {
    if (multiplyMode) {
      const videos = document.querySelectorAll('video');
      videos?.forEach(video => video?.pause());
    }

    onPauseCallback();
    if (isSharedPage) {
      CmsAPI.logEvent(id, { eventType: 'cta_click', ctaId: callAction.id });
    }

    const containsProtocol = callAction.url?.startsWith('http://') || callAction.url?.startsWith('https://');
    const resultUrl = containsProtocol ? callAction.url : `https://${callAction.url}`;

    const isValid = checkURLFormat(resultUrl);

    isValid && window.open(resultUrl, '_blank');
  };

  const checkCtaFont = async font => {
    try {
      await checkFontLoaded(font);
    } catch (e) {
      console.log('Font loaded failed with error:', e);
    }
  };


  useEffect(() => {
    const updateCalculations = () => {
      const fontSize = `${recalculateVuToPx(Math.max(minFontSize, callAction?.size))}px`;
      const borderRadius = `${recalculateVuToPx(callAction?.radius)}px`;

      const fontFamily = `'${callAction?.font?.name}'`;
      checkCtaFont(callAction?.font);

      const currentStyles = {
        fontFamily: fontFamily || 'Nunito Sans',
        fontSize,
        color: callAction?.textColor,
        background: callAction?.bgColor,
        borderRadius,
        ...objectPosition,
      };

      if (callAction.fullscreen) {
        currentStyles.boxShadow = 'none';
        const widthValue = Math.ceil(videoContentWidth);
        currentStyles.width = `${widthValue}px`;
        currentStyles.maxWidth = `${widthValue}px`;
        currentStyles.height = `${videoContentHeight - (multiplyMode ? PLAYER_BORDER_SIZE * 2 : 0)}px`;
        currentStyles.left = '50%';
        currentStyles.transform = 'translate(-50%)';
        currentStyles.padding = `0 ${videoContentWidth * (callAction?.position === POSITIONS.MIDDLE.type ? CARD_MIDDLE_PADDING_PERCENTAGE : CARD_PADDING_PERCENTAGE)}px`;
        if (currentStyles?.bottom) {
          delete currentStyles.bottom;
        }
        currentStyles.top = `${(parentNode?.offsetHeight - videoContentHeight) / 2}px`;
        if (multiplyMode) {
          currentStyles.top = `${parseInt(currentStyles.top) + PLAYER_BORDER_SIZE}px`;
        }
        if (isFullScreen) {
          currentStyles.top = '50%';
          currentStyles.transform = 'translate(-50%, -50%)';
        }
      } else {
        const isMiddlePosition = callAction?.position?.includes('middle');

        if (isMiddlePosition) {
          if (callAction?.position === 'middle') {
            currentStyles.transform = 'translate(-50%, 50%)';
          } else if (callAction?.position === 'middle_right' || callAction?.position === 'middle_left') {
            currentStyles.transform = 'translate( 0%, 50%)';
          } else if (callAction?.position?.endsWith('middle')) {
            currentStyles.transform = 'translate(-50%)';
          }

          currentStyles.width = 'max-content';
        }

        if (callAction?.position?.startsWith('bottom')) {
          // If the player controls overlap the button - show it above
          const defaultControlsHeight = isMobileView ? DEFAULT_CONTROLS_HEIGHT.MOBILE : DEFAULT_CONTROLS_HEIGHT.DESKTOP;
          if (isControlsVisible && Number(objectPosition?.bottom?.split('px')[0]) <= defaultControlsHeight) {
            currentStyles.bottom = `${Number(objectPosition.bottom?.split('px')[0]) + defaultControlsHeight}px`;
          }
        }
      }

      // If color is not a hex value or is transparent - remove the box shadow and radius
      if (!currentStyles?.background || !isHexColor(currentStyles?.background) || checkIsFullyTransparent(currentStyles?.background)) {
        currentStyles.background = 'none';
        currentStyles.boxShadow = 'none';
        currentStyles.borderRadius = 0;
      }

      setCustomStyles(currentStyles);
    };
    if (callAction && objectPosition) {
      updateCalculations();
    }
  }, [callAction, objectPosition, isControlsVisible, recalculateVuToPx, videoContentWidth, videoContentHeight]);

  useEffect(() => {
    let newOpacity = 0;
    if (currentSeconds - callAction.offset < CALL_ACTION_TRANSITION_TIME) {
      newOpacity = Math.min(1, ((currentSeconds - callAction.offset) * 0.2) / 100);
    } else {
      newOpacity = 0;
      if (currentSeconds >= callAction.offset + callAction.duration - CALL_ACTION_TRANSITION_TIME) {
        newOpacity = Math.max(0, ((callAction.offset + callAction.duration - currentSeconds) * 0.2) / 100);
      } else {
        newOpacity = 1;
      }
    }
    setOpacity(newOpacity);
  }, [currentSeconds]);

  useEffect(() => {
    if (isNewActionTimeline) {
      setOpacity(1);
    }
  }, [isNewActionTimeline]);

  useEffect(() => {
    setUpdateSimulation(true);
    setTimeout(() => {
      setUpdateSimulation(false);
    }, 50);
  }, [id]);

  return (
    !loading &&
    !!customStyles.fontSize && (
      <button
        className={cx(styles.button, { [styles.fullscreen]: callAction.fullscreen && callAction.text }, styles[callAction?.position])}
        style={{ ...customStyles, opacity }}
        disabled={callAction.url === null}
        onClick={handleClick}
        dangerouslySetInnerHTML={{ __html: buttonLabel }}
        aria-label={callAction?.text}
      />
    )
  );
};

export default VideoCallToAction;
